// src/pages/LegalPages.tsx
// -----------------------------------------------------------------------------
// Privacy, Cookies, Terms, Shipping & Returns.
//
// Adapted from Glen's (Essaness) pages - same platform, same Stripe flow, same Irish
// jurisdiction - with Red Robin's own details, carriers and shipping rates, and one
// section his predate: CUSTOMER ACCOUNTS. This shop has registration, saved carts and
// order history, which is personal data his policy never had to describe.
//
// Shop identity comes from shopConfig so the structure travels; the commercial terms
// (returns, carriers, hygiene exception) are this merchant's own and were confirmed
// by the owner.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import React from 'react';
import { shopConfig as cfg } from '../shopConfig';

const EMAIL = 'redrobinboutique@yahoo.com';
const PHONE = '+353 87 316 0963';
const VAT = 'IE7323426R';
const ADDRESS = 'Main Street, Ballinagh, Co. Cavan, H12 T9P9, Ireland';
const ACCENT = cfg.brand.accent;
const INK = cfg.brand.secondary;

const LegalPage: React.FC<{ title: string; version?: string; children: React.ReactNode }> = ({ title, version, children }) => (
  <div style={{ padding: '48px 20px 80px', maxWidth: 800, margin: '0 auto' }}>
    <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.8rem,5vw,3rem)', color: INK, margin: '0 0 8px' }}>{title}</h1>
    {version && <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(0,0,0,0.35)', margin: '0 0 28px' }}>{version}</p>}
    <div style={{ width: 48, height: 4, background: ACCENT, margin: '0 0 36px' }} />
    {children}
    <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 11, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      {cfg.shop_name} · {ADDRESS} · {EMAIL}
    </div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 30 }}>
    <h2 style={{ fontWeight: 900, fontSize: '1.05rem', color: INK, margin: '0 0 10px', fontStyle: 'italic' }}>{title}</h2>
    <div style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)' }}>{children}</div>
  </div>
);

const lead: React.CSSProperties = { fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)', marginBottom: 30 };
const link: React.CSSProperties = { color: INK, textDecoration: 'underline' };
const VERSION = `V1.0 · October 2026 · ${cfg.shop_name} · Ballinagh`;

const warn: React.CSSProperties = {
  background: `${ACCENT}14`, border: `1px solid ${ACCENT}44`,
  padding: '12px 16px', borderLeft: `4px solid ${ACCENT}`,
};

// ---------------------------------------------------------------- Privacy ----
export const PrivacyPage: React.FC = () => (
  <LegalPage title="Privacy Policy" version={VERSION}>
    <p style={lead}>
      We respect your privacy and are committed to protecting your personal data in accordance with the
      General Data Protection Regulation (GDPR).
    </p>
    <Section title="1. Data Controller">
      <p>{cfg.shop_name} (sole trader)<br />{ADDRESS}<br />
        Email: <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a><br />
        Phone: {PHONE}<br />VAT: {VAT}</p>
      <p style={{ marginTop: 8 }}>Digital infrastructure and data processing services are provided by DBOS
        (operating under Smart Trades Training, CRO No. 778363, Ireland) under a Data Processing Agreement.</p>
    </Section>
    <Section title="2. What Data We Collect">
      <p>When you use our website or make a purchase, we may collect: name, email address, phone number,
        shipping address, payment information (processed securely via Stripe - we never see or store your
        card details), and website usage data.</p>
    </Section>
    <Section title="3. Customer Accounts">
      <p>You can shop as a guest, or create an account. An account is optional and exists so you can save a
        basket for later, see it again on another device, and view your order history.</p>
      <p style={{ marginTop: 8 }}>If you create one we store your name, email address, sign-in details
        (handled by Google Firebase Authentication - we never see your password), the basket you saved, and
        your orders. Creating an account also lets us see your basket and order activity so we can help you
        with a purchase or follow up on an unfinished order - you agree to this when you register, and you
        can ask us to delete your account and its data at any time.</p>
    </Section>
    <Section title="4. How We Use Your Data">
      <p>We process your data to: fulfil your orders and deliver products, maintain your account and saved
        basket if you have one, respond to enquiries, process payments securely via Stripe, improve our
        website and service, and comply with legal obligations under Irish and EU law.</p>
    </Section>
    <Section title="5. Legal Basis">
      <p>We process data based on: contractual necessity (to fulfil your order), legitimate interest (to
        improve our service), legal obligations (GDPR, Irish consumer law), and your consent where required
        (account creation, analytics cookies, marketing).</p>
    </Section>
    <Section title="6. Data Sharing and Third-Party Processors">
      <p>We share data only with the processors required to deliver our service:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8 }}>
        <li>Stripe - payment processing (Stripe Payments Europe Ltd., 1 Grand Canal Street Lower, Dublin 2)</li>
        <li>An Post - delivery within Ireland, Northern Ireland and the United Kingdom</li>
        <li>DPD - delivery to Europe and the United States</li>
        <li>Google Firebase - authentication and hosting</li>
        <li>DBOS - digital infrastructure and hosting</li>
      </ul>
      <p style={{ marginTop: 8 }}>Data transfers outside the EU are safeguarded by Standard Contractual Clauses (SCCs).</p>
    </Section>
    <Section title="7. Data Retention">
      <p>Order data is retained for 7 years as required by Irish tax law. Account data is retained until you
        ask us to delete it. Marketing data is retained until you withdraw consent. Website usage data is
        retained for 26 months.</p>
    </Section>
    <Section title="8. Your Rights">
      <p>Under GDPR you have the right to: access your data, request correction or deletion, restrict or
        object to processing, and data portability. Contact us at <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a> to
        exercise any right. You may also lodge a complaint with the Irish Data Protection Commission at{' '}
        <a href="https://www.dataprotection.ie" target="_blank" rel="noreferrer" style={link}>dataprotection.ie</a>.</p>
    </Section>
    <Section title="9. Cookies">
      <p>We use essential cookies for shop functionality and Stripe payments. Analytics cookies are only
        activated with your explicit consent. See our <a href="/cookies" style={link}>Cookie Policy</a>.</p>
    </Section>
    <Section title="10. Changes">
      <p>We may update this policy as needed. The latest version is always published on this page.</p>
    </Section>
  </LegalPage>
);

// ---------------------------------------------------------------- Cookies ----
export const CookiesPage: React.FC = () => (
  <LegalPage title="Cookie Policy" version={VERSION}>
    <p style={lead}>This page explains how {cfg.shop_name} uses cookies and similar technologies on our website.</p>
    <Section title="1. What Are Cookies?">
      <p>Cookies are small text files stored on your device when you visit a website. They help the site
        remember your preferences and improve your experience.</p>
    </Section>
    <Section title="2. Cookies We Use">
      <p><strong>Essential cookies (always active)</strong><br />Required for the shop to function: session
        management, your basket, sign-in, and Stripe payment security. These cannot be disabled.</p>
      <p style={{ marginTop: 12 }}><strong>Analytics cookies (consent required)</strong><br />Used to
        understand how visitors use our site so we can improve it. Only activated after you give consent via
        our cookie banner.</p>
      <p style={{ marginTop: 12 }}><strong>Payment cookies</strong><br />Stripe sets cookies to ensure secure
        and compliant payment processing. These are essential for checkout.</p>
    </Section>
    <Section title="3. Legal Basis">
      <p>Essential cookies are used on the basis of legitimate interest. Analytics cookies require your
        explicit consent under GDPR. You may withdraw consent at any time by clearing your browser cookies or
        adjusting your browser settings.</p>
    </Section>
    <Section title="4. Managing Cookies">
      <p>You can manage or disable non-essential cookies through your browser settings at any time. Disabling
        essential cookies will affect shop functionality.</p>
    </Section>
    <Section title="5. Updates">
      <p>We may update this Cookie Policy periodically. The latest version is always available on this page.</p>
    </Section>
  </LegalPage>
);

// ------------------------------------------------------------------ Terms ----
export const TermsPage: React.FC = () => (
  <LegalPage title="Terms and Conditions" version={VERSION}>
    <p style={lead}>These Terms and Conditions apply to all purchases made through the {cfg.shop_name} online shop.</p>
    <Section title="1. Seller">
      <p>{cfg.shop_name} (sole trader)<br />{ADDRESS}<br />
        Email: <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a> · Phone: {PHONE}<br />VAT No: {VAT}</p>
    </Section>
    <Section title="2. Orders and Contract">
      <p>A binding contract is formed when you complete checkout and receive an order confirmation email.
        All prices include VAT at the applicable Irish rate.</p>
    </Section>
    <Section title="3. Payment">
      <p>All payments are processed securely via Stripe. We accept major credit and debit cards. Your card
        details are never stored by us.</p>
    </Section>
    <Section title="4. Accounts">
      <p>An account is optional. You are responsible for keeping your sign-in details secure. By creating an
        account you agree that we may see your basket and order activity in order to help you with a purchase.
        You can ask us to close your account and delete its data at any time.</p>
    </Section>
    <Section title="5. Shipping">
      <p>We deliver with An Post (Ireland, Northern Ireland, United Kingdom) and DPD (Europe, United States).
        Delivery times and costs are listed on our <a href="/shipping" style={link}>Shipping and Returns</a> page
        and shown at checkout. Free collection from our shop in Ballinagh is available.</p>
    </Section>
    <Section title="6. Right of Return - 14 Days">
      <p>Under EU consumer law you have the right to return most items within 14 calendar days of receipt
        without giving a reason. To arrange a return, contact us at <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a>.
        Items must be returned unused, in original condition with tags attached and in their original
        packaging. Return postage is the customer's responsibility unless the item is faulty.</p>
    </Section>
    <Section title="7. Hygiene Exception - Important">
      <p style={warn}>
        <strong>Underwear and other hygiene-sensitive garments cannot be returned</strong> once the hygiene
        seal has been removed or the item has been worn, in accordance with EU Directive 2011/83/EU
        (Article 16(e)). This does not affect your statutory rights regarding faulty goods.
      </p>
    </Section>
    <Section title="8. Faulty Items">
      <p>If an item arrives damaged or faulty, contact us within 48 hours at{' '}
        <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a> with photos. We will arrange a replacement or
        full refund at no cost to you.</p>
    </Section>
    <Section title="9. Limitation of Liability">
      <p>Our liability is limited to the value of the goods purchased. We are not liable for indirect loss,
        loss of profits, or third-party service failures such as carrier delays.</p>
    </Section>
    <Section title="10. Governing Law">
      <p>These terms are governed by the laws of Ireland. Disputes may be referred to the European Online
        Dispute Resolution platform at{' '}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" style={link}>ec.europa.eu/consumers/odr</a>.</p>
    </Section>
  </LegalPage>
);

// --------------------------------------------------------------- Shipping ----
const ROWS: [string, string, string, string][] = [
  ['Ireland', 'An Post', '2 business days', '€7.00'],
  ['Ireland - Express', 'An Post', '1 business day', '€10.00'],
  ['Northern Ireland', 'An Post', '3 business days', '€9.00'],
  ['Northern Ireland - Express', 'An Post', '1-2 business days', '€13.00'],
  ['United Kingdom', 'An Post', '3 business days', '€9.00'],
  ['United Kingdom - Express', 'An Post', '1-2 business days', '€13.00'],
  ['Europe', 'DPD', '3 business days', '€15.00'],
  ['United States', 'DPD', '7-10 business days', '€38.00'],
  ['Collection in Ballinagh', '-', 'Ready same day', 'Free'],
];

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 0', color: 'rgba(0,0,0,0.5)',
  textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10,
};

export const ShippingPage: React.FC = () => (
  <LegalPage title="Shipping and Returns" version={`${cfg.shop_name} · Ballinagh · Ireland`}>
    <Section title="Delivery">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
            <th style={th}>Destination</th>
            <th style={th}>Carrier</th>
            <th style={th}>Time</th>
            <th style={{ ...th, textAlign: 'right' }}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([dest, carrier, time, cost]) => (
            <tr key={dest} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <td style={{ padding: '10px 0', fontWeight: 600 }}>{dest}</td>
              <td style={{ padding: '10px 0', color: 'rgba(0,0,0,0.5)' }}>{carrier}</td>
              <td style={{ padding: '10px 0', color: 'rgba(0,0,0,0.5)' }}>{time}</td>
              <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 700, color: ACCENT }}>{cost}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ marginTop: 16, fontSize: 13, color: 'rgba(0,0,0,0.5)' }}>
        Delivery times are business days from dispatch and are estimates, not guarantees. You choose your
        delivery option at checkout.
      </p>
    </Section>
    <Section title="Returns - 14 Day Right of Return">
      <p>You have 14 calendar days from receipt to return most items. Items must be unused, with tags
        attached and in their original packaging. Contact{' '}
        <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a> to arrange a return. Return postage is the
        customer's responsibility unless the item is faulty.</p>
    </Section>
    <Section title="Hygiene Items - Cannot Be Returned">
      <div style={warn}>
        <p><strong>Underwear and other hygiene-sensitive garments cannot be returned</strong> once the
          hygiene seal has been removed or the item has been worn.</p>
        <p style={{ marginTop: 8 }}>This is permitted under EU Directive 2011/83/EU (Article 16(e)) and does
          not affect your statutory rights if an item is faulty.</p>
      </div>
    </Section>
    <Section title="Faulty or Incorrect Items">
      <p>If something arrives damaged, faulty or not as ordered, contact us within 48 hours at{' '}
        <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a> with photos. We will arrange a replacement or
        full refund at no cost to you.</p>
    </Section>
  </LegalPage>
);